import React, { useEffect, useState } from 'react';
import whatsappService from '../../services/whatsappService';

type LogItem = {
  id: number;
  tipo: string;
  estado: string;
  destinatarioNombre?: string;
  destinatarioNumero: string;
  detalle?: string;
  fechaEnvio: string;
};

const LogsPanel: React.FC = () => {
  const [logs, setLogs] = useState<LogItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [estado, setEstado] = useState<string>(''); // '', 'Enviada', 'Error'
  const [page, setPage] = useState<number>(1);
  const pageSize = 10;
  const [total, setTotal] = useState<number>(0);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await whatsappService.getMessageLogs({ from, to, estado: estado as any, page, pageSize });
      setLogs(data.items);
      setTotal(data.total);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al cargar historial');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [from, to, estado, page]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="bg-white shadow rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-900">Historial de mensajes enviados</h3>
        <button onClick={load} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded">Actualizar</button>
      </div>
      <p className="text-sm text-gray-600 mb-4">Se muestran solo los mensajes del tenant actual (X-Tenant-Id).</p>

      {/* Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Desde</label>
          <input type="date" value={from} onChange={(e) => { setPage(1); setFrom(e.target.value); }} className="w-full border rounded px-2 py-1" />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Hasta</label>
          <input type="date" value={to} onChange={(e) => { setPage(1); setTo(e.target.value); }} className="w-full border rounded px-2 py-1" />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Estado</label>
          <select value={estado} onChange={(e) => { setPage(1); setEstado(e.target.value); }} className="w-full border rounded px-2 py-1">
            <option value="">Todos</option>
            <option value="Enviada">Enviada</option>
            <option value="Error">Error</option>
          </select>
        </div>
        <div className="md:col-span-2 flex items-end justify-end">
          <div className="text-xs text-gray-600">{total} resultados</div>
        </div>
      </div>
      {loading ? (
        <div className="text-gray-500">Cargando...</div>
      ) : error ? (
        <div className="text-red-600 text-sm">{error}</div>
      ) : logs.length === 0 ? (
        <div className="text-gray-500 text-sm">Sin mensajes enviados aún.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Fecha</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Tipo</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Estado</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Destinatario</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Número</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Detalle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {logs.map((l) => (
                <tr key={l.id}>
                  <td className="px-3 py-2 text-gray-700">{new Date(l.fechaEnvio).toLocaleString()}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${l.tipo === 'Alerta' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                      {l.tipo}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${l.estado === 'Error' ? 'bg-red-200 text-red-800' : 'bg-green-100 text-green-700'}`}>
                      {l.estado || 'Enviada'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-700">{l.destinatarioNombre || '-'}</td>
                  <td className="px-3 py-2 text-gray-700">{l.destinatarioNumero}</td>
                  <td className="px-3 py-2 text-gray-700">{l.detalle || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginación */}
      <div className="mt-4 flex items-center justify-between">
        <div className="text-xs text-gray-600">Página {page} de {totalPages}</div>
        <div className="space-x-2">
          <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50">Anterior</button>
          <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50">Siguiente</button>
        </div>
      </div>
    </div>
  );
};

export default LogsPanel;
